import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { components } from "../../api/schema.gen";

export type AlertRule = components["schemas"]["AlertRuleDTO"];
export type AlertKind = AlertRule["kind"];
export type TriggeredAlert = components["schemas"]["TriggeredAlertDTO"];
export type AlertRuleStatus = components["schemas"]["AlertRuleStatusDTO"];

const ALERTS_KEY = ["alerts"] as const;
const ALERT_STATUS_KEY = ["alerts", "status"] as const;

export function useAlerts() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ALERTS_KEY,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET("/api/alerts", { signal });
      if (error || !data) throw new Error(String(error ?? "no data"));
      return data;
    },
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: async (vars: {
      secid: string;
      kind: AlertKind;
      threshold: number;
    }) => {
      const result = await api.POST("/api/alerts", { body: vars });
      if (result.error) throw new Error(String(result.error));
      return result.data!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
      qc.invalidateQueries({ queryKey: ALERT_STATUS_KEY });
    },
  });

  const remove = useMutation({
    mutationFn: async (rule_id: number) => {
      const result = await api.DELETE("/api/alerts/{rule_id}", {
        params: { path: { rule_id } },
      });
      if (result.error) throw new Error(String(result.error));
    },
    onMutate: async (rule_id) => {
      await qc.cancelQueries({ queryKey: ALERTS_KEY });
      await qc.cancelQueries({ queryKey: ALERT_STATUS_KEY });
      const prevRules = qc.getQueryData<AlertRule[]>(ALERTS_KEY);
      const prevStatus = qc.getQueryData<AlertRuleStatus[]>(ALERT_STATUS_KEY);
      qc.setQueryData<AlertRule[]>(ALERTS_KEY, (old) =>
        (old ?? []).filter((r) => r.id !== rule_id),
      );
      qc.setQueryData<AlertRuleStatus[]>(ALERT_STATUS_KEY, (old) =>
        (old ?? []).filter((s) => s.rule.id !== rule_id),
      );
      return { prevRules, prevStatus };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevRules) qc.setQueryData(ALERTS_KEY, ctx.prevRules);
      if (ctx?.prevStatus) qc.setQueryData(ALERT_STATUS_KEY, ctx.prevStatus);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
      qc.invalidateQueries({ queryKey: ALERT_STATUS_KEY });
    },
  });

  return {
    rules: query.data ?? [],
    isLoading: query.isPending,
    createAlert: create.mutateAsync,
    isCreating: create.isPending,
    removeAlert: (id: number) => remove.mutate(id),
    removeAlertAsync: remove.mutateAsync,
  };
}

export function useAlertsStatus() {
  return useQuery({
    queryKey: ALERT_STATUS_KEY,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET("/api/alerts/status", { signal });
      if (error || !data) throw new Error(String(error ?? "no data"));
      return data;
    },
    // котировки в кэше обновляются раз в несколько минут, дистанция меняется
    // соответственно — поллим раз в 30с, чтобы UI не казался залипшим
    refetchInterval: 30_000,
    staleTime: 0,
  });
}
